/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { type TimesheetEntry } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/hooks/useTimesheetEntry";
import { calculateAutoBreakTimes } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/utils/calculateAutoBreakTimes";
import { calculateHasPendingHoursAdjustment } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/utils/calculateHasPendingHoursAdjustment";
import { isMandatoryBreakRequired } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/utils/isMandatoryBreakRequired";
import { isAfter, isBefore, isEqual, parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetProvisionalExtraWorkedTimeRequest } from "../api/useGetProvisionalExtraWorkedTimeRequest";
import {
  isClockInAdjustment,
  isClockOutAdjustment,
  isShiftExtension,
} from "../utils/provisionalRequestDetection";

interface UsePendingHoursAdjustmentParams {
  shiftId: string | undefined;
  isMissedPunchRequestEnabledV2: boolean;
  /** The original recorded timesheet (hcp_clock_in/out). Used as the "verified" baseline. */
  formTimesheet: TimesheetEntry | undefined;
  /** The currently displayed clock-in time (may equal the form value if no adjustment). */
  formClockInTime: Date | undefined;
  formClockOutTime: Date | undefined;
  formBreakStartTime: Date | undefined;
  formBreakEndTime: Date | undefined;
  /**
   * Fallback clock-in time used as the "original" baseline when formTimesheet is undefined
   * (e.g. unverified shifts with no IP clock-in). Typically the scheduled shift start ISO string.
   */
  recordedClockInTime?: string;
  /**
   * Fallback clock-out time used as the "original" baseline when formTimesheet is undefined
   * (e.g. unverified shifts with no IP clock-out). Typically the scheduled shift end ISO string.
   */
  recordedClockOutTime?: string;
  /** Duration of the mandatory break for this facility. Used to auto-inject break into the verified baseline. */
  mandatoryBreakDurationInMinutes?: number;
  /**
   * Whether the facility requires a lunch break. Combined with shift duration, this gates whether
   * the auto-injected break should appear in the verified baseline (see `isMandatoryBreakRequired`).
   */
  requiresLunchBreak?: boolean;

  /**
   * Whether shift extension rollout functionality is on. This determines whether workers can extend their shift past
   * scheduled end.
   */
  isShiftExtendable?: boolean;
}

export interface UsePendingHoursAdjustmentResult {
  /** Date parsed from the provisional extra worked time request's start, or undefined when none exists. */
  adjustedClockInTime: Date | undefined;
  /**
   * Date parsed from the provisional extra worked time request's end, or undefined when no
   * shift-extension end is set (clock-in-only adjustments) or when the request's end matches
   * the recorded clock-out time.
   */
  adjustedClockOutTime: Date | undefined;
  /** The provisional extra worked time request entity, if one exists for this shift. */
  existingRequest:
    | {
        id: string;
        attributes: {
          start: Date;
          end?: Date;
          reason?: string;
          reasons?: { start?: string; end?: string };
        };
      }
    | undefined;
  /**
   * True when `isMissedPunchRequestEnabledV2` is on AND the adjusted timesheet claims more paid
   * minutes than the verified (original) timesheet. Use this to gate both the alert banner and the
   * Verified/Pending breakdown in TimesheetTimeEntries.
   */
  hasPendingHoursAdjustment: boolean;
  /**
   * Pass as `originalClockInTime` on TimesheetTimeEntries.
   * Only set when the feature is on and a clock-in adjustment exists.
   */
  originalClockInTime: Date | undefined;
  /**
   * Pass as `originalClockOutTime` on TimesheetTimeEntries.
   * Only set when the feature is on and the form clock-out is later than the recorded value.
   */
  originalClockOutTime: Date | undefined;
  /**
   * True when the feature is on and the break times differ from the recorded timesheet.
   * Use this to gate the `originalBreakDisplay` prop — format the string in the caller.
   */
  hasBreakAdjustment: boolean;
  /**
   * Pass as `verifiedTimesheet` on TimesheetTimeEntries.
   * Only set when the feature is on and more hours are being claimed.
   */
  verifiedTimesheetForEntries: TimesheetEntry | undefined;
  /**
   * True when a provisional extra worked time request exists with a `reasons.end` value,
   * indicating the worker extended past the scheduled shift end.
   */
  hasShiftExtension: boolean;
}

function areOptionalDatesEqual(a: Date | undefined, b: Date | undefined): boolean {
  if (!isDefined(a) && !isDefined(b)) {
    return true;
  }

  if (!isDefined(a) || !isDefined(b)) {
    return false;
  }

  return isEqual(a, b);
}

function resolveOriginalClockInTime(params: {
  isMissedPunchRequestEnabledV2: boolean;
  displayClockInTime: Date | undefined;
  recordedClockInTime: Date | undefined;
}): Date | undefined {
  const { isMissedPunchRequestEnabledV2, displayClockInTime, recordedClockInTime } = params;

  if (!isMissedPunchRequestEnabledV2) {
    return undefined;
  }

  if (!isDefined(displayClockInTime) || !isDefined(recordedClockInTime)) {
    return undefined;
  }

  return isBefore(displayClockInTime, recordedClockInTime) ? recordedClockInTime : undefined;
}

function resolveOriginalClockOutTime(params: {
  isMissedPunchRequestEnabledV2: boolean;
  adjustedClockOutTime: Date | undefined;
  formTimesheet: TimesheetEntry | undefined;
  formClockOutTime: Date | undefined;
  recordedClockOutTime: Date | undefined;
}): Date | undefined {
  const {
    isMissedPunchRequestEnabledV2,
    adjustedClockOutTime,
    formTimesheet,
    formClockOutTime,
    recordedClockOutTime,
  } = params;

  if (!isMissedPunchRequestEnabledV2) {
    return undefined;
  }

  const isLaterThanRecorded =
    isDefined(formTimesheet) &&
    isDefined(formClockOutTime) &&
    isAfter(formClockOutTime, formTimesheet.clockOutTime);

  if (isDefined(adjustedClockOutTime) || isLaterThanRecorded) {
    return formTimesheet?.clockOutTime ?? recordedClockOutTime;
  }

  return undefined;
}

/**
 * Builds a verified timesheet with auto-injected mandatory break when the original
 * timesheet has no break data and a mandatory break applies for this shift (mirroring
 * the gating the form uses in SubmitTimesheetPage via `isMandatoryBreakRequired`).
 */
interface BuildVerifiedTimesheetWithAutoBreakParams {
  formTimesheet: TimesheetEntry;
  mandatoryBreakDurationInMinutes: number | undefined;
  requiresLunchBreak: boolean;
}

function buildVerifiedTimesheetWithAutoBreak(
  params: BuildVerifiedTimesheetWithAutoBreakParams
): TimesheetEntry {
  const { formTimesheet, mandatoryBreakDurationInMinutes, requiresLunchBreak } = params;

  const hasExistingBreak =
    isDefined(formTimesheet.breakStartTime) && isDefined(formTimesheet.breakEndTime);

  if (hasExistingBreak || !isDefined(mandatoryBreakDurationInMinutes)) {
    return formTimesheet;
  }

  const shouldAutoInjectBreak = isMandatoryBreakRequired({
    requiresLunchBreak,
    clockInTime: formTimesheet.clockInTime,
    clockOutTime: formTimesheet.clockOutTime,
  });

  if (!shouldAutoInjectBreak) {
    return formTimesheet;
  }

  const autoBreak = calculateAutoBreakTimes(
    formTimesheet.clockInTime,
    formTimesheet.clockOutTime,
    mandatoryBreakDurationInMinutes
  );

  return {
    ...formTimesheet,
    breakStartTime: autoBreak?.autoBreakStartTime,
    breakEndTime: autoBreak?.autoBreakEndTime,
  };
}

/**
 * Consolidates the pending-hours-adjustment data fetching and derived state
 */
export function usePendingHoursAdjustment(
  params: UsePendingHoursAdjustmentParams
): UsePendingHoursAdjustmentResult {
  const {
    shiftId,
    isMissedPunchRequestEnabledV2,
    isShiftExtendable = false,
    formTimesheet,
    formClockInTime,
    formClockOutTime,
    formBreakStartTime,
    formBreakEndTime,
    recordedClockInTime: recordedClockInTimeIso,
    recordedClockOutTime: recordedClockOutTimeIso,
    mandatoryBreakDurationInMinutes,
    requiresLunchBreak = false,
  } = params;

  const recordedClockInTime = useMemo(
    () =>
      formTimesheet?.clockInTime ??
      (recordedClockInTimeIso ? parseISO(recordedClockInTimeIso) : undefined),
    [formTimesheet?.clockInTime, recordedClockInTimeIso]
  );
  const recordedClockOutTime = useMemo(
    () =>
      formTimesheet?.clockOutTime ??
      (recordedClockOutTimeIso ? parseISO(recordedClockOutTimeIso) : undefined),
    [formTimesheet?.clockOutTime, recordedClockOutTimeIso]
  );
  const { data: provisionalExtraWorkedTimeData } = useGetProvisionalExtraWorkedTimeRequest(
    shiftId ?? "",
    {
      enabled:
        (isMissedPunchRequestEnabledV2 || isShiftExtendable) &&
        isDefined(shiftId) &&
        shiftId.length > 0,
    }
  );
  const existingRequest = provisionalExtraWorkedTimeData?.data[0];

  const adjustedClockInTime = useMemo(() => {
    if (!existingRequest) {
      return undefined;
    }

    const existingRequestStart =
      typeof existingRequest.attributes.start === "string"
        ? parseISO(existingRequest.attributes.start)
        : existingRequest.attributes.start;

    if (
      isDefined(recordedClockInTime) &&
      !isClockInAdjustment(existingRequestStart, recordedClockInTime)
    ) {
      return undefined;
    }

    return existingRequestStart;
  }, [existingRequest, recordedClockInTime]);

  const adjustedClockOutTime = useMemo(() => {
    if (!existingRequest) {
      return undefined;
    }

    const existingRequestEnd = existingRequest.attributes.end;
    if (!isDefined(existingRequestEnd)) {
      return undefined;
    }

    const parsedEnd =
      typeof existingRequestEnd === "string" ? parseISO(existingRequestEnd) : existingRequestEnd;

    if (!isClockOutAdjustment(parsedEnd, recordedClockOutTime)) {
      return undefined;
    }

    return parsedEnd;
  }, [existingRequest, recordedClockOutTime]);

  const displayClockInTime = adjustedClockInTime ?? formClockInTime;
  const displayClockOutTime = adjustedClockOutTime ?? formClockOutTime;

  const verifiedBaseline = useMemo(() => {
    if (!isDefined(formTimesheet)) {
      return undefined;
    }

    return buildVerifiedTimesheetWithAutoBreak({
      formTimesheet,
      mandatoryBreakDurationInMinutes,
      requiresLunchBreak,
    });
  }, [formTimesheet, mandatoryBreakDurationInMinutes, requiresLunchBreak]);

  const rawHasPendingHoursAdjustment =
    isDefined(verifiedBaseline) && isDefined(displayClockInTime) && isDefined(displayClockOutTime)
      ? calculateHasPendingHoursAdjustment({
          verifiedTimesheet: verifiedBaseline,
          adjustedTimesheet: {
            clockInTime: displayClockInTime,
            clockOutTime: displayClockOutTime,
            breakStartTime: formBreakStartTime,
            breakEndTime: formBreakEndTime,
          },
        })
      : false;

  // Incorporate the feature flag so all callers can use hasPendingHoursAdjustment directly
  // without needing to re-apply the flag check themselves.
  const hasPendingHoursAdjustment =
    (isMissedPunchRequestEnabledV2 || isShiftExtendable) && rawHasPendingHoursAdjustment;

  const originalClockOutTime = resolveOriginalClockOutTime({
    isMissedPunchRequestEnabledV2,
    adjustedClockOutTime,
    formTimesheet,
    formClockOutTime,
    recordedClockOutTime,
  });

  const hasBreakAdjustment =
    isMissedPunchRequestEnabledV2 &&
    isDefined(formTimesheet) &&
    (!areOptionalDatesEqual(formTimesheet.breakStartTime, formBreakStartTime) ||
      !areOptionalDatesEqual(formTimesheet.breakEndTime, formBreakEndTime));

  return {
    adjustedClockInTime,
    adjustedClockOutTime,
    existingRequest,
    hasPendingHoursAdjustment,
    hasBreakAdjustment,
    hasShiftExtension: isShiftExtension(existingRequest?.attributes?.reasons),
    originalClockInTime: resolveOriginalClockInTime({
      isMissedPunchRequestEnabledV2,
      displayClockInTime,
      recordedClockInTime,
    }),
    originalClockOutTime,
    verifiedTimesheetForEntries: hasPendingHoursAdjustment ? verifiedBaseline : undefined,
  };
}
/* eslint-enable max-lines */
